/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Box, Stack } from "@towersystems/towersystems-design-system";
import classnames from "classnames";
import { findIndex, memoize } from "lodash";
import { get, find } from "lodash";
import AsyncSelect from "react-select/async";
/**
 * Internal dependencies
 */
import { getFilters, countFilters } from "state/ui/dashboard-filter/selectors";
import { setFilters } from "state/ui/dashboard-filter/actions";
import Icon from "components/icon";
import { createFetchOptionsFn } from "./utils";
import { ButtonV2 } from "components/button-v2";
import { DropdownLinkV2 } from "components/dropdown-link-v2";
import { Text } from "components/text";

/**
 * Style dependencies
 */
import "./styles.scss";

const departmentFn = createFetchOptionsFn(
  "Department.name",
  "Department.department_code",
);
const supplierFn = createFetchOptionsFn(
  "Supplier.name",
  "Supplier.supplier_code",
);
const categoryFn = createFetchOptionsFn(
  "Category.name",
  "Category.category_code",
);
const productFn = createFetchOptionsFn("Product.name", "Product.stock_code");

const DashboardFilters = ({ count, filters: defaultFilters, ...props }) => {
  const [filters, setFilters] = useState(defaultFilters || []);

  useEffect(() => {
    resetFilters(defaultFilters);
  }, [defaultFilters]);

  const applyFilters = () => {
    props.setFilters(filters);
  };

  const classes = classnames("dashboard__filters");

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const createHandleOnChangeFn = React.useCallback(
    (name) =>
      memoize((selectedOptions) => {
        let updatedFilters = [...filters];

        // Check if all selected options have the same idDimension
        const allHaveIdDimension =
          selectedOptions.length > 0 &&
          selectedOptions.every((s) => s.idDimension);
        let member, values;

        if (allHaveIdDimension) {
          // all have the same idDimension, use the idDimension as member and idDimensionValue as value
          member = selectedOptions[0].idDimension;
          values = selectedOptions.map((s) => s.idDimensionValue);
        } else {
          // fallback to passed-in name and the value field
          member = name;
          values = selectedOptions.map((s) => s.value);
        }

        let index = findIndex(filters, { member });

        if (index < 0) {
          updatedFilters.push({ member, operator: "equals", values });
        } else {
          updatedFilters[index] = { member, operator: "equals", values };
        }

        setFilters(updatedFilters);
      }),
    [filters],
  );

  return (
    <Box className={classes}>
      <DropdownLinkV2
        activatorProps={{
          compact: true,
        }}
        title={
          <div className="flex items-center justify-center">
            <Icon size={20} name="RiFilterLine" marginRight={5} />
            <Text variant="headingXxs">
              Filter deck {count >= 1 && ` (${count})`}
            </Text>
          </div>
        }
        className="dashboard__filter-dropdown-link"
        onClose={resetFilters}
      >
        {({ close }) => (
          <Box padding="large">
            <Box marginBottom="large">
              <Text variant="headingXs">Filter by</Text>
            </Box>
            <Stack space="large" padding="large">
              <Box>
                <Text variant="headingXxs">Department</Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    name="Department.department_code"
                    loadOptions={departmentFn}
                    defaultOptions
                    placeholder="Search.."
                    onChange={createHandleOnChangeFn(
                      "Department.department_code",
                    )}
                    value={get(
                      find(filters, { member: "Department.department_code" }),
                      "values",
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text variant="headingXxs">Category</Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    name="Category.category_code"
                    placeholder="Search.."
                    defaultOptions
                    loadOptions={categoryFn}
                    onChange={createHandleOnChangeFn("Category.category_code")}
                    value={get(
                      find(filters, { member: "Category.category_code" }),
                      "values",
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text variant="headingXxs">Supplier</Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    placeholder="Search.."
                    name="Supplier.supplier_code"
                    loadOptions={supplierFn}
                    defaultOptions
                    onChange={createHandleOnChangeFn("Supplier.supplier_code")}
                    value={get(
                      find(filters, { member: "Supplier.supplier_code" }),
                      "values",
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text variant="headingXxs">Product</Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    name="Product.stock_code"
                    placeholder="Search.."
                    loadOptions={productFn}
                    defaultOptions
                    onChange={createHandleOnChangeFn("Product.stock_code")}
                    value={get(
                      find(filters, { member: "Product.stock_code" }),
                      "values",
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
            </Stack>
            <div className="flex flex-row gap-3">
              <ButtonV2
                onClick={() => {
                  applyFilters();
                  close();
                }}
                fullWidth
                variant="primary"
                filled={true}
              >
                Apply
              </ButtonV2>
              <ButtonV2
                onClick={() => {
                  close();
                }}
                fullWidth
              >
                Cancel
              </ButtonV2>
            </div>
            <Box marginTop="large">
              <Text variant="bodyXs" tone="subdued">
                * Filters will only apply to applicable reports.
              </Text>
            </Box>
            <Box marginTop="medium">
              <Text variant="bodyXs" tone="subdued">
                ** Filters set here will overrride filters set on individual
                reports.
              </Text>
            </Box>
          </Box>
        )}
      </DropdownLinkV2>
    </Box>
  );
};

export default connect(
  (state) => ({
    filters: getFilters(state),
    count: countFilters(state),
  }),
  { setFilters },
)(DashboardFilters);
